// Admin → /admin/themes — CMS dashboard for homepage discovery themes.
//
// Reuses StatCard / StatusBadge primitives from Phase 1 and SparklineCard for
// impressions/clicks. Mobile-first responsive grid throughout.
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Plus, Edit3, Trash2, Copy, ArrowUp, ArrowDown, Eye, EyeOff, Search,
  Image as ImageIcon, Sparkles, X, Save, Tag, Calendar, ExternalLink,
} from 'lucide-react';
import { api, formatApiErrorDetail } from '../../lib/api';
import { StatCard, StatusBadge, EmptyState } from './partnersPrimitives';

// ── Helpers ──────────────────────────────────────────────────────────
const STATUS_OPTIONS = ['draft', 'published', 'scheduled', 'archived'];
const STATUS_COLOR = {
  published: 'bg-green-100 text-green-800 border-green-300',
  draft:     'bg-gray-100 text-gray-700 border-gray-300',
  scheduled: 'bg-blue-100 text-blue-700 border-blue-300',
  archived:  'bg-amber-100 text-amber-800 border-amber-300',
};

const Field = ({ label, children, span = 1 }) => (
  <label className={`flex flex-col gap-1 min-w-0 sm:col-span-${span}`}>
    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">{label}</span>
    {children}
  </label>
);

const baseInput = 'rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:outline-none';

// ── Editor (drawer) ──────────────────────────────────────────────────
const ThemeEditor = ({ open, onClose, value, meta, onSaved }) => {
  const [form, setForm] = useState(value || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(value || {});
  }, [value, open]);

  if (!open) return null;
  const isEdit = !!form.id;
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const setNum = (k) => (e) => setForm((s) => ({ ...s, [k]: Number(e.target.value) }));
  const setBool = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.checked }));

  const save = async () => {
    if (!form.title || form.title.trim().length < 2) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      // strip CMS-only fields
      delete payload.analytics; delete payload.live_now;
      const r = isEdit
        ? await api.put(`/admin/themes/${form.id}`, payload)
        : await api.post('/admin/themes', payload);
      toast.success(isEdit ? 'Theme updated' : 'Theme created');
      onSaved(r.data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70]" data-testid="theme-editor">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside className="absolute top-0 right-0 h-full w-full sm:w-[640px] max-w-full bg-white dark:bg-gray-900 shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 h-14 border-b border-gray-200 dark:border-gray-800">
          <h2 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-red-600" />
            {isEdit ? 'Edit theme' : 'New theme'}
          </h2>
          <button onClick={onClose} className="tap-target p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Close" data-testid="theme-editor-close">
            <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Visual preview */}
          <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800 relative h-44 sm:h-56 bg-gray-100 dark:bg-gray-800">
            {form.image ? (
              <img src={form.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 grid place-items-center text-gray-400 text-xs">
                <ImageIcon className="h-8 w-8" />
              </div>
            )}
            <div className={`absolute inset-0 bg-gradient-to-t ${form.accent || 'from-red-500/30 to-red-900/80'}`} />
            <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4 text-white">
              <h3 className="text-base sm:text-lg font-bold drop-shadow-lg">{form.title || 'Theme title'}</h3>
              <p className="text-[11px] sm:text-xs text-white/85 drop-shadow">{form.subtitle || 'Theme subtitle'}</p>
            </div>
          </div>

          {/* Core */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Title">
              <input value={form.title || ''} onChange={set('title')} className={baseInput} data-testid="theme-field-title" />
            </Field>
            <Field label="Subtitle">
              <input value={form.subtitle || ''} onChange={set('subtitle')} className={baseInput} data-testid="theme-field-subtitle" />
            </Field>
            <Field label="Group" span={1}>
              <select value={form.group || 'travel-themes'} onChange={set('group')} className={baseInput} data-testid="theme-field-group">
                {(meta?.groups || []).map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </Field>
            <Field label="Status" span={1}>
              <select value={form.status || 'draft'} onChange={set('status')} className={baseInput} data-testid="theme-field-status">
                {(meta?.statuses || STATUS_OPTIONS).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Priority (lower = first)" span={1}>
              <input type="number" min={1} max={1000} value={form.priority ?? 50} onChange={setNum('priority')} className={baseInput} data-testid="theme-field-priority" />
            </Field>
            <Field label="Featured" span={1}>
              <label className="flex items-center gap-2 h-10 text-sm">
                <input type="checkbox" checked={!!form.featured} onChange={setBool('featured')} className="h-4 w-4 accent-red-600" />
                Mark as featured
              </label>
            </Field>
            <Field label="Homepage visible" span={1}>
              <label className="flex items-center gap-2 h-10 text-sm">
                <input type="checkbox" checked={form.homepage_visible !== false} onChange={setBool('homepage_visible')} className="h-4 w-4 accent-red-600" />
                Show on homepage
              </label>
            </Field>
            <Field label="Description" span={2}>
              <textarea value={form.description || ''} onChange={set('description')} rows={2} className={baseInput} data-testid="theme-field-description" />
            </Field>
          </div>

          {/* Media */}
          <div>
            <p className="text-xs uppercase font-semibold tracking-wider text-gray-500 mb-2">Media</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Desktop image URL"><input value={form.image || ''} onChange={set('image')} className={baseInput} data-testid="theme-field-image" /></Field>
              <Field label="Mobile image URL"><input value={form.mobile_image || ''} onChange={set('mobile_image')} className={baseInput} /></Field>
              <Field label="Thumbnail URL"><input value={form.thumbnail || ''} onChange={set('thumbnail')} className={baseInput} /></Field>
              <Field label="Fallback URL"><input value={form.fallback_image || ''} onChange={set('fallback_image')} className={baseInput} /></Field>
              <Field label="Accent gradient" span={2}>
                <select value={form.accent || ''} onChange={set('accent')} className={baseInput}>
                  <option value="">— select preset —</option>
                  {(meta?.accent_presets || []).map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
            </div>
          </div>

          {/* CTA */}
          <div>
            <p className="text-xs uppercase font-semibold tracking-wider text-gray-500 mb-2">CTA</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="CTA label"><input value={form.cta_label || ''} onChange={set('cta_label')} className={baseInput} placeholder="Explore" /></Field>
              <Field label="CTA URL" span={2}><input value={form.cta_url || ''} onChange={set('cta_url')} className={baseInput} placeholder="/hotels/search?city=Goa" /></Field>
              <Field label="Variant">
                <select value={form.cta_variant || 'primary'} onChange={set('cta_variant')} className={baseInput}>
                  {(meta?.cta_variants || ['primary', 'secondary', 'ghost']).map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </Field>
            </div>
          </div>

          {/* Scheduling */}
          <div>
            <p className="text-xs uppercase font-semibold tracking-wider text-gray-500 mb-2 flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" /> Scheduling (UTC)
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Start at"><input type="datetime-local" value={form.start_at?.slice(0, 16) || ''} onChange={(e) => setForm((s) => ({ ...s, start_at: e.target.value ? new Date(e.target.value).toISOString() : null }))} className={baseInput} /></Field>
              <Field label="End at"><input type="datetime-local" value={form.end_at?.slice(0, 16) || ''} onChange={(e) => setForm((s) => ({ ...s, end_at: e.target.value ? new Date(e.target.value).toISOString() : null }))} className={baseInput} /></Field>
            </div>
          </div>

          {/* Tags */}
          <Field label="Tags (comma-separated)">
            <input
              value={(form.tags || []).join(', ')}
              onChange={(e) => setForm((s) => ({ ...s, tags: e.target.value.split(',').map((t) => t.trim()).filter(Boolean) }))}
              className={baseInput}
              placeholder="monsoon, kerala, hills"
            />
          </Field>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="theme-editor-save" className="inline-flex items-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white px-5 py-2.5 text-sm font-semibold shadow">
            <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save theme'}
          </button>
        </div>
      </aside>
    </div>
  );
};

// ── Main page ────────────────────────────────────────────────────────
const AdminThemes = () => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [meta, setMeta] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (groupFilter) params.group = groupFilter;
      const r = await api.get('/admin/themes', { params });
      setItems(r.data.items || []);
    } catch (e) {
      toast.error('Failed to load themes');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, groupFilter]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/admin/themes/meta');
        setMeta(r.data);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const total = items.length;
    const published = items.filter((i) => i.status === 'published').length;
    const draft = items.filter((i) => i.status === 'draft').length;
    const liveNow = items.filter((i) => i.live_now).length;
    const totalImpressions = items.reduce((a, i) => a + (i.analytics?.impressions || 0), 0);
    const totalClicks = items.reduce((a, i) => a + (i.analytics?.clicks || 0), 0);
    return { total, published, draft, liveNow, totalImpressions, totalClicks };
  }, [items]);

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const order = [...items];
    const [m] = order.splice(idx, 1);
    order.splice(target, 0, m);
    setItems(order);
    try {
      await api.post('/admin/themes/reorder', { order: order.map((i) => i.id) });
      toast.success('Order updated');
    } catch {
      toast.error('Reorder failed');
      load();
    }
  };

  const duplicate = async (id) => {
    try {
      await api.post(`/admin/themes/${id}/duplicate`);
      toast.success('Theme duplicated as draft');
      load();
    } catch { toast.error('Duplicate failed'); }
  };
  const togglePublish = async (t) => {
    try {
      await api.post(`/admin/themes/${t.id}/${t.status === 'published' ? 'unpublish' : 'publish'}`);
      toast.success(t.status === 'published' ? 'Unpublished' : 'Published');
      load();
    } catch { toast.error('Status change failed'); }
  };
  const remove = async (id) => {
    if (!confirm('Delete this theme? This cannot be undone.')) return;
    try { await api.delete(`/admin/themes/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-themes-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-red-600" />
            Travel Themes CMS
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage homepage discovery campaigns, seasonal collections and featured promotions.</p>
        </div>
        <button onClick={() => { setEditing({ status: 'draft', priority: 50, accent: 'from-red-500/30 to-red-900/80', group: 'travel-themes', homepage_visible: true }); setEditorOpen(true); }}
                data-testid="theme-new-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 text-sm font-semibold shadow tap-target">
          <Plus className="h-4 w-4" /> New theme
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard label="Total" value={stats.total} accent="gray" testId="theme-stat-total" />
        <StatCard label="Published" value={stats.published} accent="green" testId="theme-stat-published" />
        <StatCard label="Drafts" value={stats.draft} accent="amber" testId="theme-stat-draft" />
        <StatCard label="Live now" value={stats.liveNow} accent="red" testId="theme-stat-live" sub="Within schedule window" />
        <StatCard label="Engagement" value={`${stats.totalClicks} / ${stats.totalImpressions}`} accent="blue" testId="theme-stat-engagement" sub="clicks / impressions" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by title…" data-testid="theme-search" className={`${baseInput} pl-9 w-full`} />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={baseInput} data-testid="theme-filter-status">
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)} className={baseInput} data-testid="theme-filter-group">
          <option value="">All groups</option>
          {(meta?.groups || []).map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>

      {/* List */}
      {loading ? (
        <p className="text-sm text-gray-500">Loading themes…</p>
      ) : items.length === 0 ? (
        <EmptyState title="No themes yet" description="Create your first homepage campaign — it will appear in the public Travel Themes carousel once published." action={
          <button onClick={() => { setEditing({}); setEditorOpen(true); }} className="inline-flex items-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 text-sm font-semibold shadow">
            <Plus className="h-4 w-4" /> Create theme
          </button>
        } />
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t, idx) => (
            <li key={t.id} data-testid={`theme-card-${t.id}`} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden flex flex-col">
              <div className="relative h-32 sm:h-40 bg-gray-100 dark:bg-gray-800">
                {t.image && <img src={t.image} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />}
                <div className={`absolute inset-0 bg-gradient-to-t ${t.accent || 'from-red-500/30 to-red-900/80'}`} />
                <div className="absolute top-2 left-2 flex gap-1.5">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_COLOR[t.status] || STATUS_COLOR.draft}`}>{t.status}</span>
                  {t.live_now && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-600 text-white">LIVE</span>}
                  {t.featured && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-400 text-amber-900">★</span>}
                </div>
                <div className="absolute inset-x-0 bottom-0 p-3 text-white">
                  <h3 className="font-bold drop-shadow truncate">{t.title}</h3>
                  <p className="text-[11px] opacity-90 drop-shadow truncate">{t.subtitle || '—'}</p>
                </div>
              </div>
              <div className="p-3 flex-1 flex flex-col gap-2 text-xs text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-1.5 truncate">
                  <Tag className="h-3 w-3" />
                  <span>{t.group}</span>
                  <span>·</span>
                  <span>priority {t.priority}</span>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                  <Eye className="h-3 w-3" /> {t.analytics?.impressions || 0}
                  <ExternalLink className="h-3 w-3" /> {t.analytics?.clicks || 0}
                </div>
                <div className="mt-auto flex items-center gap-1 pt-2 border-t border-gray-100 dark:border-gray-800">
                  <button data-testid={`theme-edit-${t.id}`} onClick={() => { setEditing(t); setEditorOpen(true); }} title="Edit" className="tap-target p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800"><Edit3 className="h-4 w-4 text-gray-600" /></button>
                  <button onClick={() => togglePublish(t)} title={t.status === 'published' ? 'Unpublish' : 'Publish'} className="tap-target p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800">{t.status === 'published' ? <EyeOff className="h-4 w-4 text-gray-600" /> : <Eye className="h-4 w-4 text-green-600" />}</button>
                  <button onClick={() => duplicate(t.id)} title="Duplicate" className="tap-target p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800"><Copy className="h-4 w-4 text-gray-600" /></button>
                  <button onClick={() => move(idx, -1)} title="Move up" disabled={idx === 0} className="tap-target p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30"><ArrowUp className="h-4 w-4 text-gray-600" /></button>
                  <button onClick={() => move(idx, 1)} title="Move down" disabled={idx === items.length - 1} className="tap-target p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30"><ArrowDown className="h-4 w-4 text-gray-600" /></button>
                  <button data-testid={`theme-delete-${t.id}`} onClick={() => remove(t.id)} title="Delete" className="tap-target p-2 rounded hover:bg-red-50 ml-auto"><Trash2 className="h-4 w-4 text-red-600" /></button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ThemeEditor open={editorOpen} value={editing} meta={meta} onClose={() => setEditorOpen(false)} onSaved={() => load()} />
    </div>
  );
};

export default AdminThemes;
